import React, { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonSelect,
  FooterActionBar,
  Icon,
  InlineMessage,
  ModalIsland,
} from '@saviynt/design-system';

import {
  PERSONALIZATION_DEFAULT_CONFIG,
  WIDGETS,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  usePersonalization,
  useTypedSelector,
} from '../../../../shared/hooks';
import { violationsTabActions } from '../../data-store/violationsTabSlice';

import { type ViolationsWidgetMenuItemOption } from './ViolationsWidgetMenuItem';
import ViolationsWidgetMenuList from './ViolationsWidgetMenuList';

import './ViolationsWidgetMenu.css';

const ACTIVE_WIDGET_COUNT = 6;

interface ViolationsWidgetMenuProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (state: boolean) => void;
}

/**
 * ViolationsWidgetMenu
 */
function ViolationsWidgetMenu({
  isMenuOpen,
  setIsMenuOpen,
}: ViolationsWidgetMenuProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const { widgets } = useTypedSelector(
    (state) => state.aag.violationsTab.controls
  );

  const { savePersonlization } = usePersonalization();

  // Constants
  const isWidgetOverflow = useMemo(
    () =>
      widgets.filter((widget) => widget.isChecked).length >=
      ACTIVE_WIDGET_COUNT,
    [widgets]
  );

  const handleMenuChange = useCallback(
    (widgetList: ViolationsWidgetMenuItemOption[]) => {
      dispatch(
        violationsTabActions.setWidgets(
          widgetList.map((widget) => ({
            ...WIDGETS[widget.value],
            ...widget,
          }))
        )
      );
    },
    [dispatch]
  );

  return (
    <Box className='Sod-ViolationsWidgetMenu-wrapper'>
      <ButtonSelect
        size='large'
        kind='rectangle'
        label={l('Customize Widgets')}
        onClick={() => {
          setIsMenuOpen(true);
        }}
        isOpen={isMenuOpen}
        isVisualCueEnabled={false}
        prefixIcon={<Icon kind='Settings' size='medium' />}
      />
      <ModalIsland
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isOutsideClickEnabled
        title={l('Customize Widgets')}
        subtitle={l(
          'Select up to {count} widgets to be displayed and drag to reorder them.',
          { count: ACTIVE_WIDGET_COUNT }
        )}
        headerIcon={<Icon kind='Settings' size='large' />}
        className='Sod-ViolationsWidgetMenu-modal'
        classNames={{
          bodyClassName: 'Sod-ViolationsWidgetMenu-contentWrapper',
        }}
        contentBody={
          <Box className='Sod-ViolationsWidgetMenu-content'>
            <ViolationsWidgetMenuList
              options={widgets.map((widget) => ({
                ...widget,
                label: l(widget.label),
              }))}
              isDisabled={isWidgetOverflow}
              handleChange={handleMenuChange}
            />
          </Box>
        }
        FooterActionBarComp={
          <FooterActionBar size='large'>
            <div className='Sod-ViolationsWidgetMenu-footerSpacer' />
            <div className='Sod-ViolationsWidgetMenu-footerWrapper'>
              {isWidgetOverflow && (
                <InlineMessage
                  text={l("You've reached the max number")}
                  colorTheme='info'
                  size='medium'
                  leftIcon={<Icon kind='AlertInfoFilled' />}
                  className='Sod-ViolationsWidgetMenu-footerInlineMessage'
                />
              )}
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={() => {
                  setIsMenuOpen(false);
                  savePersonlization({
                    ...PERSONALIZATION_DEFAULT_CONFIG,
                    saveWidget: false,
                  });
                }}>
                {l('Cancel')}
              </Button>
              <Button
                type='button'
                kind='filled'
                size='medium'
                onClick={() => {
                  setIsMenuOpen(false);
                  savePersonlization(PERSONALIZATION_DEFAULT_CONFIG);
                }}>
                {l('Save')}
              </Button>
            </div>
          </FooterActionBar>
        }
      />
    </Box>
  );
}

export default memo(ViolationsWidgetMenu);
